import { useState, type ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  BarChart2,
  Box,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  FileText,
  Grid,
  Layout,
  Map,
  Server,
  Settings,
  Sliders,
  User,
  Users,
} from "react-feather";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { Tooltip } from "@bfc-software/pallet";

import styles from "./outbound-sidebar.module.css";

/**
 * One entry in the rail.
 *
 * `to` being absent is the whole of what "not built yet" means here. Rather than
 * a separate `isDisabled` flag that could disagree with it, an entry without a
 * destination renders as an inert row - so an entry cannot claim to be
 * navigable and have nowhere to go.
 */
interface NavEntry {
  /** Stable key, and what `data-nav-item` carries for tests. */
  id: string;
  /** Key under `sidebar.links` in the outbound namespace. */
  labelKey: string;
  icon: ReactNode;
  /**
   * Route path to link to, carrying `$lang`, `$org` and `$location`. Omitted
   * while the screen does not exist.
   */
  to?: string;
}

interface NavGroup {
  id: string;
  /** Key under `sidebar.groups`. Absent for the leading, unlabelled group. */
  labelKey?: string;
  icon?: ReactNode;
  entries: ReadonlyArray<NavEntry>;
}

const ICON_SIZE = 18;
const CHILD_ICON_SIZE = 16;

/**
 * Outbound's navigation, in the order the design puts it.
 *
 * Only Tasks, Items, Locations, Zones, User Groups and Selectors have a destination today. The rest
 * are listed deliberately rather than added as they land: the rail is how the product
 * tells the user what it covers, and a rail that grows an entry per release
 * reads as a different product each time.
 */
const NAV: ReadonlyArray<NavGroup> = [
  {
    id: "main",
    entries: [
      {
        id: "dashboard",
        labelKey: "dashboard",
        icon: <Grid size={ICON_SIZE} strokeWidth={1.9} />,
      },
      {
        id: "tasks",
        labelKey: "tasks",
        icon: <CheckSquare size={ICON_SIZE} strokeWidth={1.9} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/tasks",
      },
      {
        id: "items",
        labelKey: "items",
        icon: <Box size={ICON_SIZE} strokeWidth={1.9} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/items",
      },
      {
        id: "locations",
        labelKey: "locations",
        icon: <Layout size={ICON_SIZE} strokeWidth={1.9} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/locations",
      },
      {
        id: "analytics",
        labelKey: "analytics",
        icon: <BarChart2 size={ICON_SIZE} strokeWidth={1.9} />,
      },
    ],
  },
  {
    id: "setup",
    labelKey: "setup",
    icon: <Layout size={ICON_SIZE} strokeWidth={1.9} />,
    entries: [
      {
        id: "zones",
        labelKey: "zones",
        icon: <Map size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/zones",
      },
      {
        id: "user-groups",
        labelKey: "userGroups",
        icon: <Users size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/user-groups",
      },
      {
        id: "selectors",
        labelKey: "selectors",
        icon: <User size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
        to: "/$lang/orgs/$org/outbound/whs/$location/selectors",
      },
    ],
  },
  {
    id: "admin",
    labelKey: "admin",
    icon: <Settings size={ICON_SIZE} strokeWidth={1.9} />,
    entries: [
      {
        id: "system-controls",
        labelKey: "systemControls",
        icon: <Sliders size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
      },
      {
        id: "ftp-log",
        labelKey: "ftpLog",
        icon: <Server size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
      },
      {
        id: "audit-log",
        labelKey: "auditLog",
        icon: <FileText size={CHILD_ICON_SIZE} strokeWidth={1.8} />,
      },
    ],
  },
];

/**
 * A navigable entry.
 *
 * `activeProps` rather than a match check of our own: the router already knows
 * which route is active, and computing it here would go stale the moment a
 * screen gains a child route.
 */
function NavLink({
  entry,
  label,
  collapsed,
  params,
}: {
  entry: NavEntry & { to: string };
  label: string;
  collapsed: boolean;
  params: { lang: string; org: string; location: string };
}) {
  return (
    <Link
      to={entry.to}
      params={params}
      className={styles.entry}
      activeProps={{ className: styles.active }}
      data-nav-item={entry.id}
      title={collapsed ? label : undefined}
    >
      <span className={styles.bar} aria-hidden="true" />
      <span className={styles.icon}>{entry.icon}</span>
      <span className={styles.label}>{label}</span>
    </Link>
  );
}

/**
 * An entry with nowhere to go.
 *
 * A `<span>` rather than a disabled `<button>`: there is no action to disable,
 * and a button would take focus on the way to the one link that works. The
 * tooltip is what tells a user why the row does nothing, so it is not decoration
 * - but it is suppressed while collapsed, where the icon already carries a
 * `title` for the label itself.
 */
function NavPlaceholder({
  entry,
  label,
  collapsed,
  reason,
}: {
  entry: NavEntry;
  label: string;
  collapsed: boolean;
  reason: string;
}) {
  const row = (
    <span
      className={styles.entry}
      data-nav-item={entry.id}
      data-not-built="true"
      aria-disabled="true"
      title={collapsed ? label : undefined}
    >
      <span className={styles.bar} aria-hidden="true" />
      <span className={styles.icon}>{entry.icon}</span>
      <span className={styles.label}>{label}</span>
    </span>
  );

  return collapsed ? row : <Tooltip content={reason}>{row}</Tooltip>;
}

/**
 * Outbound's primary navigation: a persistent rail beside the content, not the
 * drawer Engine Room uses.
 *
 * The two are deliberately different shapes. Engine Room is a set of unrelated
 * internal tools reached occasionally, so a drawer that gets out of the way
 * suits it. Outbound is a floor manager's day - moving between tasks, items and
 * locations is the work - so its navigation stays on screen and shows where you
 * are without being opened first.
 *
 * Lives here rather than in pallet until a second product wants it. Promoting it
 * early would mean guessing which parts are Outbound's and which are the
 * pattern's, and every guess has to be undone by whoever tries to reuse it.
 */
export function OutboundSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { lang, org, location } = useParams({ strict: false });

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  // The rail only makes sense inside a warehouse, and every destination needs
  // all three. Rendering it without them would produce links the router cannot
  // build.
  if (
    typeof lang !== "string" ||
    typeof org !== "string" ||
    typeof location !== "string"
  ) {
    return <></>;
  }

  const params = { lang, org, location };
  const reason = t("sidebar.notBuilt");

  return (
    <nav
      className={styles.rail}
      data-collapsed={collapsed}
      aria-label={t("sidebar.ariaLabel")}
      data-test-role="outbound-sidebar"
    >
      {NAV.map((group) => (
        <div key={group.id} className={styles.group}>
          {group.labelKey && (
            <div className={styles.groupHeading} data-nav-group={group.id}>
              <span className={styles.icon}>{group.icon}</span>
              <span className={styles.label}>
                {t(`sidebar.groups.${group.labelKey}`)}
              </span>
            </div>
          )}
          {group.entries.map((entry) => {
            const label = t(`sidebar.links.${entry.labelKey}`);
            const child = Boolean(group.labelKey);

            return (
              <div
                key={entry.id}
                className={child ? styles.childRow : styles.row}
              >
                {entry.to ? (
                  <NavLink
                    entry={{ ...entry, to: entry.to }}
                    label={label}
                    collapsed={collapsed}
                    params={params}
                  />
                ) : (
                  <NavPlaceholder
                    entry={entry}
                    label={label}
                    collapsed={collapsed}
                    reason={reason}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className={styles.spacer} />

      <button
        type="button"
        className={styles.collapseToggle}
        onClick={() => setCollapsed((previous) => !previous)}
        aria-label={t(collapsed ? "sidebar.expand" : "sidebar.collapse")}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </nav>
  );
}
